import React, { useEffect, useState, useCallback } from 'react';
import { Plus, BookCopy, Search, Trash2, Edit2 } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { format, parseISO } from 'date-fns';

export default function Library() {
  const { user } = useAuthStore();
  const canManage = ['admin', 'superadmin', 'registrar'].includes(user?.role || '');
  const [tab, setTab] = useState(canManage ? 'catalog' : 'my-books');

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Library</h1><p className="page-subtitle">Browse the catalog and manage borrowed books</p></div>
      </div>
      <div className="tabs">
        <button className={`tab ${tab === 'catalog' ? 'active' : ''}`} onClick={() => setTab('catalog')}>Catalog</button>
        {canManage && <button className={`tab ${tab === 'issues' ? 'active' : ''}`} onClick={() => setTab('issues')}>Issued Books</button>}
        <button className={`tab ${tab === 'my-books' ? 'active' : ''}`} onClick={() => setTab('my-books')}>My Books</button>
      </div>
      {tab === 'catalog' && <Catalog canManage={canManage} />}
      {tab === 'issues' && canManage && <Issues />}
      {tab === 'my-books' && <MyBooks />}
    </div>
  );
}

function Catalog({ canManage }: { canManage: boolean }) {
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showBookModal, setShowBookModal] = useState<any>(null);
  const [showIssueModal, setShowIssueModal] = useState<any>(null);

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/library/books', { params: { search } }); setBooks(r.data.data.books || []); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchBooks, 300);
    return () => clearTimeout(t);
  }, [fetchBooks]);

  const removeBook = async (id: string) => {
    if (!window.confirm('Remove this book from the catalog?')) return;
    try { await api.delete(`/library/books/${id}`); toast.success('Book removed'); fetchBooks(); }
    catch { toast.error('Failed to remove book'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Search by title, author, ISBN, category..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {canManage && <button className="btn btn-primary" onClick={() => setShowBookModal({})}><Plus size={16} />Add Book</button>}
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : books.length === 0 ? (
        <div className="card"><div className="empty-state"><BookCopy size={48} /><p>No books found.</p></div></div>
      ) : (
        <div className="grid-3">
          {books.map(b => (
            <div key={b.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ fontWeight: 700, fontSize: 15 }}>{b.title}</h3>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{b.author || 'Unknown author'}</p>
                </div>
                <span className={`badge badge-${b.available_copies > 0 ? 'success' : 'danger'}`}>{b.available_copies}/{b.total_copies} available</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {b.category && <span className="badge badge-info">{b.category}</span>}
                {b.isbn && <span className="badge badge-gray">ISBN {b.isbn}</span>}
                {b.shelf_location && <span className="badge badge-gray">Shelf {b.shelf_location}</span>}
              </div>
              {canManage && (
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowBookModal(b)}><Edit2 size={13} />Edit</button>
                  <button className="btn btn-primary btn-sm" disabled={b.available_copies < 1} onClick={() => setShowIssueModal(b)}>Issue</button>
                  <button onClick={() => removeBook(b.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}><Trash2 size={16} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showBookModal && <BookModal book={showBookModal} onClose={() => setShowBookModal(null)} onSaved={() => { setShowBookModal(null); fetchBooks(); }} />}
      {showIssueModal && <IssueModal book={showIssueModal} onClose={() => setShowIssueModal(null)} onSaved={() => { setShowIssueModal(null); fetchBooks(); }} />}
    </div>
  );
}

function BookModal({ book, onClose, onSaved }: any) {
  const isEdit = !!book.id;
  const [form, setForm] = useState<any>({ total_copies: 1, ...book });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    setLoading(true);
    try {
      if (isEdit) await api.put(`/library/books/${book.id}`, form);
      else await api.post('/library/books', form);
      toast.success(isEdit ? 'Book updated' : 'Book added');
      onSaved();
    } catch { toast.error('Failed to save book'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>{isEdit ? 'Edit Book' : 'Add Book'}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" required value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Author</label><input className="form-input" value={form.author || ''} onChange={e => set('author', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">ISBN</label><input className="form-input" value={form.isbn || ''} onChange={e => set('isbn', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Category</label><input className="form-input" value={form.category || ''} onChange={e => set('category', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Shelf Location</label><input className="form-input" value={form.shelf_location || ''} onChange={e => set('shelf_location', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Total Copies</label><input type="number" min={1} className="form-input" value={form.total_copies} onChange={e => set('total_copies', parseInt(e.target.value) || 1)} /></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : (isEdit ? 'Save Changes' : 'Add Book')}</button></div>
        </form>
      </div>
    </div>
  );
}

function IssueModal({ book, onClose, onSaved }: any) {
  const [borrowerType, setBorrowerType] = useState<'student' | 'staff'>('student');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const endpoint = borrowerType === 'student' ? '/students' : '/staff';
      const r = await api.get(endpoint, { params: { search, limit: 8 } });
      setResults(borrowerType === 'student' ? (r.data.data.students || []) : (r.data.data.staff || []));
    }, 300);
    return () => clearTimeout(t);
  }, [search, borrowerType]);

  const submit = async () => {
    if (!selected) { toast.error('Select a borrower first'); return; }
    setLoading(true);
    try {
      const payload: any = { book_id: book.id };
      if (borrowerType === 'student') payload.student_id = selected.id;
      else payload.staff_id = selected.id;
      await api.post('/library/issues', payload);
      toast.success('Book issued — due in 14 days');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to issue book'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Issue "{book.title}"</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Borrower type</label>
            <select className="form-input form-select" value={borrowerType} onChange={e => { setBorrowerType(e.target.value as any); setSelected(null); setResults([]); setSearch(''); }}>
              <option value="student">Student</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Search {borrowerType}</label>
            <input className="form-input" placeholder="Name or number..." value={search} onChange={e => { setSearch(e.target.value); setSelected(null); }} />
          </div>
          {results.length > 0 && !selected && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {results.map((r: any) => (
                <div key={r.id} onClick={() => setSelected(r)} style={{ padding: '8px 12px', cursor: 'pointer', borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.first_name} {r.last_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.student_number || r.staff_number || r.email}</div>
                </div>
              ))}
            </div>
          )}
          {selected && (
            <div className="badge badge-info" style={{ alignSelf: 'flex-start', padding: '6px 12px' }}>
              Selected: {selected.first_name} {selected.last_name} ({selected.student_number || selected.staff_number})
            </div>
          )}
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={loading || !selected} onClick={submit}>{loading ? <span className="loader" /> : 'Issue Book'}</button></div>
      </div>
    </div>
  );
}

function Issues() {
  const [issues, setIssues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('issued');

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/library/issues', { params: filter ? { status: filter } : {} }); setIssues(r.data.data.issues || []); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  const returnBook = async (id: string) => {
    try {
      const r = await api.patch(`/library/issues/${id}/return`);
      toast.success(r.data.message || 'Book returned');
      fetchIssues();
    } catch { toast.error('Failed to process return'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['issued','Currently Issued'],['overdue','Overdue'],['returned','Returned'],['','All']].map(([val,label]) => (
          <button key={val} className={`btn btn-sm ${filter===val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(val)}>{label}</button>
        ))}
      </div>
      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : issues.length === 0 ? (
        <div className="card"><div className="empty-state"><BookCopy size={48} /><p>No records found.</p></div></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--bg)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Book</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Borrower</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Due</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>Status</th>
              <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, fontWeight: 600 }}>Action</th>
            </tr></thead>
            <tbody>
              {issues.map((i: any) => {
                const overdue = i.status === 'issued' && new Date(i.due_date) < new Date();
                const borrowerName = i.student_first_name ? `${i.student_first_name} ${i.student_last_name} (${i.student_number})` : `${i.staff_first_name} ${i.staff_last_name}`;
                return (
                  <tr key={i.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px', fontSize: 13 }}>{i.title}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13 }}>{borrowerName}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13 }}>{format(parseISO(i.due_date), 'd MMM yyyy')}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span className={`badge badge-${i.status === 'returned' ? 'success' : overdue ? 'danger' : 'warning'}`}>{i.status === 'returned' ? 'Returned' : overdue ? 'Overdue' : 'Issued'}</span>
                      {Number(i.fine_amount) > 0 && <span className="badge badge-danger" style={{ marginLeft: 6 }}>Fine KES {i.fine_amount}</span>}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      {i.status !== 'returned' && <button className="btn btn-secondary btn-sm" onClick={() => returnBook(i.id)}>Mark Returned</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MyBooks() {
  const [issues, setIssues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/library/my-books').then(r => setIssues(r.data.data.issues || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>;
  if (issues.length === 0) return <div className="card"><div className="empty-state"><BookCopy size={48} /><p>You haven't borrowed any books yet.</p></div></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {issues.map((i: any) => {
        const overdue = i.status === 'issued' && new Date(i.due_date) < new Date();
        return (
          <div key={i.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: 14 }}>{i.title}</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{i.author}</p>
              <p style={{ fontSize: 12, marginTop: 4 }}>Due: {format(parseISO(i.due_date), 'd MMM yyyy')}{i.returned_at && ` · Returned ${format(parseISO(i.returned_at), 'd MMM yyyy')}`}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span className={`badge badge-${i.status === 'returned' ? 'success' : overdue ? 'danger' : 'warning'}`}>{i.status === 'returned' ? 'Returned' : overdue ? 'Overdue' : 'Borrowed'}</span>
              {Number(i.fine_amount) > 0 && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>Fine: KES {i.fine_amount}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
